/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  AutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { Button, Divider, MenuItem, Typography } from '@material-ui/core'
import { useEffect, useMemo, useState } from 'react'

import {
  addCommaToPrice,
  convertPersianNumbersToEnglishNumbers,
  removeCommaFromPrice
} from '../../../../helpers/numbers'
import { getTranslate, lang } from '../../../../localization'
import { CRSStatus, PMSStatus } from '../../../../types/booking'
import { ChangeReserveDetailsInput } from '../../../../types/changeReserveDetails'
import { useStyles } from './styles/index.styles'

type Props = {
  title: string
  customizedForRoom?: boolean
  changeReserveInput: ChangeReserveDetailsInput
  setChangeReserveInput: React.Dispatch<
    React.SetStateAction<ChangeReserveDetailsInput>
  >
  roomIndex: number
  reservationNumber: string
}
export default function EditForm({
  title,
  customizedForRoom,
  roomIndex,
  changeReserveInput,
  setChangeReserveInput,
  reservationNumber
}: Props) {
  const classes = useStyles()
  const [penaltyAmount, setPenaltyAmount] = useState<string>('')
  const penaltyAmountWithComma = useMemo(
    () => addCommaToPrice(penaltyAmount),
    [penaltyAmount]
  )

  const handleChange = (newValue: string) => {
    newValue = convertPersianNumbersToEnglishNumbers(newValue)
    newValue = removeCommaFromPrice(newValue)
    setPenaltyAmount(newValue)

    if (customizedForRoom) {
      setChangeReserveInput((oldState) => ({
        ...oldState,
        rooms: [
          ...oldState.rooms.slice(0, roomIndex),
          {
            ...oldState.rooms[roomIndex],
            penaltyAmount: Number(newValue)
          },
          ...oldState.rooms.slice(roomIndex + 1)
        ]
      }))
    } else {
      setChangeReserveInput((oldState) => ({
        ...oldState,
        rooms: oldState.rooms.map((room) => ({
          ...room,
          penaltyAmount: Number(penaltyAmount)
        }))
      }))
    }
  }

  const onClickApplyToAll = () => {
    setChangeReserveInput((oldState) => ({
      ...oldState,
      CRSStatus: oldState.CRSStatus,
      PMSStatus: oldState.PMSStatus,
      rooms: oldState.rooms.map((room) => ({
        ...room,
        roomCRSStatus: oldState.CRSStatus,
        roomPMSStatus: oldState.PMSStatus,
        penaltyAmount: Number(penaltyAmount)
      }))
    }))
  }

  const options = [
    {
      value: 'Cancelled',
      label: 'Cancelled'
    },
    {
      value: 'Confirmed',
      label: 'Confirmed'
    },

    {
      value: 'NoShow',
      label: 'NoShow'
    }
  ]

  return (
    <div className={classes.container}>
      <div className={classes.top}>
        <Typography variant="h4" className={classes.blackText}>
          {title}
        </Typography>
      </div>
      <Divider />
      <div
        className={[
          classes.bottom,
          customizedForRoom ? classes.single : ''
        ].join(' ')}>
        <SelectWithLabel
          noHelperText
          label={getTranslate('CRS Status')}
          fullWidth
          onChange={(e) => {
            const newValue = e.target.value as CRSStatus
            setChangeReserveInput((oldState) => {
              if (customizedForRoom && roomIndex >= 0) {
                const rooms = [...oldState.rooms]
                rooms[roomIndex] = {
                  ...rooms[roomIndex],
                  roomCRSStatus: newValue
                }
                return { ...oldState, rooms }
              } else {
                return { ...oldState, CRSStatus: newValue }
              }
            })
          }}
          value={
            customizedForRoom
              ? changeReserveInput.rooms[roomIndex]?.roomCRSStatus ?? ''
              : changeReserveInput.CRSStatus ?? ''
          }
          size="small">
          {options.map((item, index) => (
            <MenuItem key={index} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </SelectWithLabel>

        <SelectWithLabel
          noHelperText
          fullWidth
          label={getTranslate('PMS Status')}
          onChange={(e) => {
            const newValue = e.target.value as PMSStatus
            setChangeReserveInput((oldState) => {
              if (customizedForRoom && roomIndex >= 0) {
                const rooms = [...oldState.rooms]
                rooms[roomIndex] = {
                  ...rooms[roomIndex],
                  roomPMSStatus: newValue
                }
                return { ...oldState, rooms }
              } else {
                return { ...oldState, PMSStatus: newValue }
              }
            })
          }}
          value={
            customizedForRoom
              ? changeReserveInput.rooms[roomIndex]?.roomPMSStatus ?? ''
              : changeReserveInput.PMSStatus ?? ''
          }
          size="small">
          {options.map((item, index) => (
            <MenuItem key={index} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </SelectWithLabel>
        <InputWithLabel
          style={lang === 'fa' ? { fontFamily: 'IranSans' } : undefined}
          noHelperText
          inputSize={'small'}
          label={getTranslate('Cancellation Penalty Price')}
          placeholder={getTranslate('Please Enter Cancel Penalty')}
          value={penaltyAmountWithComma ? penaltyAmountWithComma : ''}
          onChange={(event) => {
            handleChange(event.currentTarget.value)
          }}
        />
        {!customizedForRoom && (
          <Button
            variant="contained"
            color="primary"
            className={classes.btn}
            onClick={onClickApplyToAll}>
            <Typography className={classes.btnText}>
              {getTranslate('Apply to all')}
            </Typography>
          </Button>
        )}
      </div>
    </div>
  )
}
